import * as THREE from 'three'
import type { GlbDatumPlaneFrame } from './resolve-frame'

/**
 * 创建 datum-slot 平面线框对象。
 *
 * @param color 线框颜色。
 * @returns 平面线框对象。
 */
export function createPlaneOutline(color: number): THREE.LineSegments {
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(24), 3))

  const material = new THREE.LineBasicMaterial({
    color,
    depthTest: false,
    depthWrite: false,
    transparent: true,
    opacity: 1,
  })

  const outline = new THREE.LineSegments(geometry, material)
  outline.name = 'glb-datum-slot-plane-outline'
  outline.frustumCulled = false
  outline.userData.isGlbDatumSlotOverlay = true
  outline.raycast = () => undefined
  return outline
}

/**
 * 将 datum-slot 平面 frame 更新到线框对象。
 *
 * @param frame 平面 frame。
 * @param color 线框颜色。
 * @param outline 复用中的线框对象。
 * @returns 线框对象。
 */
export function buildPlaneOutline(
  frame: GlbDatumPlaneFrame,
  color: number,
  outline?: THREE.LineSegments,
): THREE.LineSegments {
  const target = outline ?? createPlaneOutline(color)
  updatePlaneOutline(target, frame, color)
  return target
}

/**
 * 将 datum-slot 平面 frame 更新到线框对象。
 *
 * @param outline 线框对象。
 * @param frame 平面 frame。
 * @param color 线框颜色。
 * @returns 线框对象。
 */
export function updatePlaneOutline(
  outline: THREE.LineSegments,
  frame: GlbDatumPlaneFrame,
  color: number,
): THREE.LineSegments {
  const halfX = frame.xDir.clone().multiplyScalar(frame.width * 0.5)
  const halfY = frame.yDir.clone().multiplyScalar(frame.height * 0.5)
  const leftBottom = frame.origin.clone().sub(halfX).sub(halfY)
  const rightBottom = frame.origin.clone().add(halfX).sub(halfY)
  const rightTop = frame.origin.clone().add(halfX).add(halfY)
  const leftTop = frame.origin.clone().sub(halfX).add(halfY)

  updatePlaneOutlinePositions(outline.geometry, [
    ...leftBottom.toArray(), ...rightBottom.toArray(),
    ...rightBottom.toArray(), ...rightTop.toArray(),
    ...rightTop.toArray(), ...leftTop.toArray(),
    ...leftTop.toArray(), ...leftBottom.toArray(),
  ])

  const material = outline.material
  if (material instanceof THREE.LineBasicMaterial) {
    material.color.setHex(color)
  }

  outline.visible = true
  return outline
}

/**
 * 更新平面线框的几何顶点。
 * 说明：
 * 1. 通道对象需要保持引用稳定，所以这里直接写入既有 position attribute；
 * 2. 只在容量不匹配时重建 attribute，避免每次切换都替换几何对象。
 *
 * @param geometry 线框几何体。
 * @param positions 顶点数组。
 */
function updatePlaneOutlinePositions(
  geometry: THREE.BufferGeometry,
  positions: number[],
): void {
  const existingAttribute = geometry.getAttribute('position')
  if (!(existingAttribute instanceof THREE.BufferAttribute) || existingAttribute.array.length !== positions.length) {
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    return
  }

  const positionArray = existingAttribute.array as Float32Array
  positionArray.set(positions)
  existingAttribute.needsUpdate = true
}
